<template>
  <section>
    <v-progress-linear
      v-if="state.isLoadingVehicles"
      indeterminate
    />

    <AppPageTitleBar
      class="is-hidden-sm-up"
      :title="pageTitle"
      :back-to="eventDetailRoute"
      back-label="Natrag"
    >
      <template #actions>
        <ToolbarActionButton
          v-if="canEditEvents"
          text="Odaberi vozila"
          :icon="null"
          :disabled="isEventArchived"
          :to="selectVehiclesRoute"
        />
      </template>
    </AppPageTitleBar>

    <section
      v-if="state.eventDetail"
      class="page-stack grid-stack grid-gap-page-section"
    >
      <article class="page-panel page-panel--mobile-unframed grid-stack grid-gap-20">
        <div class="grid grid-sm-up grid-row-gap-0 grid-column-gap-16 align-items-center">
          <router-link
            class="back-link col-12 col-sm-6"
            :to="eventDetailRoute"
            aria-label="Natrag"
          >
            <v-icon
              icon="mdi-arrow-left"
              size="20"
            />
            <span>Natrag</span>
          </router-link>

          <ToolbarActionButton
            v-if="canEditEvents"
            class="col-12 col-sm-6 justify-self-end"
            text="Odaberi vozila"
            :icon="null"
            :disabled="isEventArchived"
            :to="selectVehiclesRoute"
          />
        </div>

        <header class="event-context-heading">
          <h2 class="event-context-heading__title">
            Vozila
            <span class="event-context-heading__event-name">"{{ state.eventDetail.name }}"</span>
          </h2>
        </header>

        <v-table class="table vehicles-table">
          <thead class="text-mid">
            <tr>
              <th class="vehicles-table__brand">Marka</th>
              <th class="vehicles-table__model">Model i tip</th>
              <th class="vehicles-table__plate">Registarska oznaka</th>
              <th class="vehicles-table__chassis">Broj šasije</th>
              <th class="vehicles-table__color">Boja</th>
              <th />
            </tr>
          </thead>

          <tbody class="text-mid">
            <template
              v-for="vehicle in paginatedVehicles"
              :key="vehicle.id"
            >
              <tr>
                <td>{{ getEventBrandName(vehicle.brandCode) }}</td>
                <td>{{ vehicle.name }}</td>
                <td>{{ vehicle.plate }}</td>
                <td>{{ vehicle.vin }}</td>
                <td>{{ vehicle.color }}</td>
                <td class="text-right">
                  <v-btn
                    :aria-expanded="isVehicleExpanded(vehicle.id)"
                    :aria-label="`${isVehicleExpanded(vehicle.id) ? 'Sakrij' : 'Prikaži'} povijest vozača za ${getEventBrandName(vehicle.brandCode)} ${vehicle.name}`"
                    class="button button--icon"
                    :icon="isVehicleExpanded(vehicle.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    size="small"
                    variant="text"
                    @click="toggleVehicleHistory(vehicle.id)"
                  />
                </td>
              </tr>

              <tr v-if="isVehicleExpanded(vehicle.id)">
                <td
                  class="vehicles-table__history-cell"
                  colspan="6"
                >
                  <section class="driver-history">
                    <h3 class="driver-history__title body-medium-bold">
                      Vozači na testnoj vožnji:
                    </h3>

                    <v-table
                      v-if="vehicle.driverHistory.length"
                      class="table driver-history__table"
                    >
                      <thead class="text-mid">
                        <tr>
                          <th>Ime i prezime</th>
                          <th>Početak</th>
                          <th>Komentar</th>
                          <th>Završetak</th>
                          <th>Komentar</th>
                        </tr>
                      </thead>
                      <tbody class="text-mid">
                        <tr
                          v-for="driver in vehicle.driverHistory"
                          :key="driver.id"
                        >
                          <td>{{ driver.name }}</td>
                          <td>{{ formatDateTime(driver.startTimestamp) }}</td>
                          <td>
                            <Tooltip :text="driver.startComment" />
                          </td>
                          <td>{{ formatDateTime(driver.endTimestamp) }}</td>
                          <td>
                            <Tooltip :text="driver.endComment" />
                          </td>
                        </tr>
                      </tbody>
                    </v-table>

                    <p
                      v-else
                      class="driver-history__empty text-mid text-center"
                    >
                      Nema evidentiranih testnih vožnji za ovo vozilo.
                    </p>
                  </section>
                </td>
              </tr>
            </template>

            <tr v-if="!paginatedVehicles.length">
              <td
                class="table__empty"
                colspan="6"
              >
                Nema vozila dodanih ovom događaju.
              </td>
            </tr>
          </tbody>
        </v-table>

        <Pagination
          :page="state.currentPage"
          :per-page="vehiclesPerPage"
          :total-results="eventVehicles.length"
          @page-change="setVehicleListPage"
        />
      </article>
    </section>
  </section>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import Pagination from '../../components/Pagination.vue'
import Tooltip from '../../components/Tooltip.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { hasPermission, permissionNames } from '../../auth/authorization'
import { formatDateTime, getEventBrandName } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showRequestErrorToast } from '../../store/appStore'

defineOptions({
  name: 'Vehicles',
})

const route = useRoute()
const vehiclesPerPage = 10

const state = reactive({
  currentPage: 1,
  eventDetail: null,
  expandedVehicleId: null,
  isLoadingVehicles: false,
})

const eventId = computed(() => route.params.eventId)
const isEventArchived = computed(() => state.eventDetail?.isArchived ?? false)
const canEditEvents = computed(() => hasPermission(permissionNames.eventsEdit))
const pageTitle = computed(() => state.eventDetail ? `Vozila "${state.eventDetail.name}"` : 'Vozila')
const eventDetailRoute = computed(() => ({
  name: 'event-detail',
  params: { eventId: eventId.value },
}))
const selectVehiclesRoute = computed(() => ({
  name: 'event-vehicles-select',
  params: { eventId: eventId.value },
}))

const eventVehicles = computed(() => state.eventDetail?.vehicles.map((vehicle) => ({
  ...vehicle,
  driverHistory: state.eventDetail.invitees.flatMap((invitee) => invitee.activities
    .filter((activity) => activity.vehicleId === vehicle.id)
    .map((activity) => ({
      id: activity.id,
      name: invitee.fullName,
      startTimestamp: new Date(activity.startedAt).getTime(),
      startComment: activity.commentStarted,
      endTimestamp: activity.finishedAt ? new Date(activity.finishedAt).getTime() : null,
      endComment: activity.commentFinished,
    }))),
})) ?? [])
const paginatedVehicles = computed(() => {
  const startIndex = (state.currentPage - 1) * vehiclesPerPage
  return eventVehicles.value.slice(startIndex, startIndex + vehiclesPerPage)
})

watch(eventId, loadEventVehiclesAsync, { immediate: true })

async function loadEventVehiclesAsync () {
  state.currentPage = 1
  state.expandedVehicleId = null
  state.isLoadingVehicles = true

  try {
    state.eventDetail = await apiClient.getAsync(`/api/events/${eventId.value}`)
  } catch (error) {
    console.error('Failed to load event vehicles.', error)
    showRequestErrorToast(error, 'Vozila nije bilo moguće učitati.')
  } finally {
    state.isLoadingVehicles = false
  }
}

function setVehicleListPage (selectedPage) {
  state.currentPage = selectedPage
  state.expandedVehicleId = null
}

function toggleVehicleHistory (vehicleId) {
  state.expandedVehicleId = isVehicleExpanded(vehicleId) ? null : vehicleId
}

function isVehicleExpanded (vehicleId) {
  return state.expandedVehicleId === vehicleId
}

</script>

<style
  scoped
  lang="scss"
>
.vehicles-table {
  --table-min-width: 820px;

  &__brand {
    width: 15%;
  }

  &__model {
    width: 16%;
  }

  &__plate {
    width: 17%;
  }

  &__chassis {
    width: 20%;
  }

  &__color {
    width: 10%;
  }

  &__history-cell:last-child {
    width: auto;
    padding: 10px 0;
  }
}

.driver-history {
  padding: 12px;
  border-radius: 6px;
  background: var(--color-surface-hover);

  &__title {
    margin: 0 0 8px;
  }

  &__table {
    --table-actions-width: auto;

    background: transparent;
  }

  &__empty {
    margin: 0;
    padding: 20px 0;
    color: var(--color-text-muted);
  }
}
</style>
